import { APP_NAME } from "@/lib/brand";
import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Cloud, Link2, Unlink, AlertTriangle, CheckCircle2, FolderOpen } from "lucide-react";
import { FolderPicker } from "./FolderPicker";

const STATUS_STYLE = {
  connected: "text-emerald-800 bg-emerald-50 border-emerald-200",
  not_configured: "text-amber-800 bg-amber-50 border-amber-200",
  not_connected: "text-slate-700 bg-slate-50 border-slate-200",
  incomplete: "text-amber-800 bg-amber-50 border-amber-200",
  future: "text-slate-500 bg-slate-50 border-slate-200",
};

export const ProviderCard = ({ cfg, providers, set, setConn, onRefresh }) => {
  const [picking, setPicking] = useState(false);
  const st = cfg.provider_status || {};
  const provider = providers.find((p) => p.key === cfg.provider);
  const connected = st.status === "connected";

  const connect = async () => {
    try { const { data } = await api.get(`/admin/backup/connect/${cfg.provider}`); window.location.href = data.auth_url; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const disconnect = async () => {
    try { await api.post("/admin/backup/disconnect"); toast.success("Account disconnected"); onRefresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4 min-w-0" data-testid="backup-provider-card">
      <div className="flex flex-wrap items-start justify-between gap-3 min-w-0">
        <div className="min-w-0"><h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Cloud className="h-4 w-4 text-slate-500" />Backup destination</h2><p className="text-xs text-slate-500">Choose where this workspace's project evidence is mirrored.</p></div>
        <Badge variant="outline" className={STATUS_STYLE[st.status] || STATUS_STYLE.not_connected} data-testid="backup-provider-status-badge">
          {connected ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <AlertTriangle className="h-3 w-3 mr-1" />}{(st.status || "").replace(/_/g, " ")}
        </Badge>
      </div>

      <div>
        <Label className="text-xs">Provider</Label>
        <Select value={cfg.provider} onValueChange={(v) => set({ provider: v })}>
          <SelectTrigger data-testid="backup-provider-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {providers.map((p) => <SelectItem key={p.key} value={p.key} disabled={!p.available} data-testid={`backup-provider-option-${p.key}`}>{p.label}{!p.available ? " [Future / Not connected]" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        {provider && <p className="text-[11px] text-slate-500 mt-1">{provider.note}</p>}
      </div>

      <div className={`rounded-lg border p-3 text-sm ${connected ? "border-emerald-200 bg-emerald-50/50" : "border-amber-200 bg-amber-50/60"}`} data-testid="backup-connection-message">
        <div className="font-medium text-slate-900">{st.message}</div>
        {st.status === "not_configured" && <p className="text-xs text-slate-600 mt-1">Microsoft authentication (Entra app registration) is not configured on this server. Register an app with redirect URI <code className="font-mono text-[11px] bg-white px-1 rounded border break-all">{providers.redirect_uri}</code> and provide MS_CLIENT_ID / MS_CLIENT_SECRET. No backups are simulated.</p>}
        <div className="flex gap-2 mt-2">
          {!connected && st.status !== "future" && <Button size="sm" onClick={connect} disabled={st.status === "not_configured"} data-testid="backup-connect-btn"><Link2 className="h-3.5 w-3.5 mr-1.5" />Connect {provider?.label}</Button>}
          {connected && <Button size="sm" variant="outline" onClick={disconnect} data-testid="backup-disconnect-btn"><Unlink className="h-3.5 w-3.5 mr-1.5" />Disconnect</Button>}
        </div>
      </div>

      {cfg.provider === "sharepoint" && (
        <div className="grid grid-cols-2 gap-3" data-testid="backup-sharepoint-fields">
          <div className="col-span-2"><Label className="text-xs">SharePoint site URL</Label><Input value={cfg.connection.site_url || ""} onChange={(e) => setConn("site_url", e.target.value)} placeholder="https://company.sharepoint.com/sites/Projects" data-testid="backup-siteurl-input" /></div>
          <div><Label className="text-xs">Document library</Label><Input value={cfg.connection.library || ""} onChange={(e) => setConn("library", e.target.value)} placeholder="Documents" data-testid="backup-library-input" /></div>
          <div><Label className="text-xs">Entra tenant ID (optional)</Label><Input value={cfg.connection.tenant_id || ""} onChange={(e) => setConn("tenant_id", e.target.value)} data-testid="backup-tenant-input" /></div>
        </div>
      )}

      <div>
        <Label className="text-xs">Destination folder (backup root)</Label>
        <div className="flex gap-2">
          <Input className="font-mono" value={cfg.destination.root_folder} onChange={(e) => set({ destination: { ...cfg.destination, root_folder: e.target.value } })} placeholder={APP_NAME} data-testid="backup-destination-input" />
          <Button variant="outline" size="icon" disabled={!connected} onClick={() => setPicking(true)} title={connected ? "Browse folders" : "Connect the provider to browse folders"} data-testid="backup-browse-btn"><FolderOpen className="h-4 w-4" /></Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-1 font-mono" data-testid="backup-destination-display">{cfg.destination_display}</p>
      </div>
      {picking && <FolderPicker onClose={() => setPicking(false)} onPick={(path) => { set({ destination: { ...cfg.destination, root_folder: path } }); setPicking(false); }} />}
    </div>
  );
};
